/* eslint-disable @1js/office-online-rules/no-v8-imports-for-fluentui */
import type { ITheme } from "@fluentui/react";
import { memoizeFunction, mergeStyleSets } from "@fluentui/react";
import type { StandardControlSemanticColors } from "@1js/acui-common";
import {
  commonReducedMotionStyles,
  getActionControlSemanticColorsWithTheme,
  getStandardControlSemanticColorsWithTheme,
  prefersReducedMotionDurationMs,
} from "@1js/acui-common";
import { customTokens, isNextStyleEnabled } from "@1js/acui-theme";
import {
  vNextRibbonModeSwitcherLeftRightMargin,
  vNextTopBottomMargin,
} from "../..";
import type { IButtonStyles } from "@fluentui/react";
import {
  bottomBarEnterTransitionDurationMs,
  chevronWidth,
} from "../RibbonBottomBar";

export interface RibbonModeChevronClassNames {
  chevronContainer: string;
}

export interface RibbonModeChevronClassNamesOptions {
  // height of the bar we are rendering the chevron in
  ribbonBarHeight: number;

  // Custom styles to override the defaults for the ribbon bottom bar
  customStyles?: string;
}

export const getRibbonModeChevronClassNames = memoizeFunction(
  (
    theme: ITheme,
    styleOptions: RibbonModeChevronClassNamesOptions
  ): RibbonModeChevronClassNames => {
    const { defaultBackgroundColor } =
      getActionControlSemanticColorsWithTheme(theme);

    return mergeStyleSets(
      {
        chevronContainer: {
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          alignItems: "flex-end",
          lineHeight: "16px",
          height: styleOptions.ribbonBarHeight,
          // Ensure that tabContentContainer slides in/out behind the mode chevron
          zIndex: 1,
          backgroundColor: defaultBackgroundColor,
        },
      },
      { chevronContainer: styleOptions.customStyles },
      isNextStyleEnabled() && getRibbonBottomBarStylesNext(styleOptions)
    );
  }
);

const getRibbonBottomBarStylesNext = (
  styleOptions: RibbonModeChevronClassNamesOptions
): RibbonModeChevronClassNames => {
  const heightWithoutMargin =
    styleOptions.ribbonBarHeight - vNextTopBottomMargin * 2;

  return mergeStyleSets({
    chevronContainer: {
      height: heightWithoutMargin,
      paddingTop: vNextTopBottomMargin,
      paddingBottom: vNextTopBottomMargin,
      paddingRight: vNextRibbonModeSwitcherLeftRightMargin,
      paddingLeft: vNextRibbonModeSwitcherLeftRightMargin,
      backgroundColor: customTokens.colorNeutralBackground1,
    },
  });
};

export const getModeChevronStyles = memoizeFunction(
  (
    isMultiline: boolean,
    theme: ITheme,
    reduceAnimations?: boolean,
    shyRibbonEnabled?: boolean
  ): IButtonStyles => {
    const duration = reduceAnimations
      ? prefersReducedMotionDurationMs
      : bottomBarEnterTransitionDurationMs;
    const standardControlSemanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);
    return {
      root: [
        {
          height: 16,
          width: chevronWidth,
          padding: 0,
        },
      ],
      icon: [
        {
          height: 12,
          width: 12,
          lineHeight: 12,
          fill: standardControlSemanticColors.textColor,
        },
        !shyRibbonEnabled && {
          transition: `transform ${duration}ms cubic-bezier(0.1, 0.9, 0.2, 1)`,
          transform: isMultiline ? "rotate(180deg)" : undefined,
        },
        commonReducedMotionStyles,
      ],
    };
  }
);
